import { Fragment, ReactNode, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import classNames from 'classnames';
import { useMatomo } from '../../../utils/matomo';
import styles from './traffic-info-accordion.module.scss';
import filterStyles from './traffic-info-filter.module.scss';
import { ReactComponent as AccordionChevron } from '../../../assets/icons/icon-accordion-chevron.svg';
import { ReactComponent as CalendarDate } from '../../../assets/icons/icon-calendar-date-empty.svg';
import { ReactComponent as CalendarTime } from '../../../assets/icons/icon-calendar-time.svg';
import dayjs from 'dayjs';
import { TrafficInfoFilterItem, TrafficInfoResponse } from '../traffic-info-types';
import { getFilterByLineName } from '../traffic-info-utils';
import { TrafficInfoBody } from './traffic-info-body';
import { useTrafficInfoContext } from '../traffic-info-context';
import { useMediaQuery } from '@mui/system';

const isCurrentHash = (id: string | undefined): boolean => window.location.hash === '#' + id;

function buildTags(
  info: TrafficInfoResponse['infos'][number],
  filters: TrafficInfoFilterItem[]
): TrafficInfoAccordionTag[] {
  const result: TrafficInfoAccordionTag[] = [];

  for (const line of info.affectedLines) {
    if (!line.number) {
      continue;
    }
    if (result.find((l) => l.label === line.number)) {
      continue;
    }

    const filter = getFilterByLineName(line.name, filters);
    if (!filter) {
      continue;
    }

    let className = filter.className;

    // Different style for "Schnellbus"
    if (line.name.startsWith('SB ')) {
      className = filterStyles.TrafficInfoFilterItemFastBus;
    }
    // Different style for "Metrobus"
    if (line.number.startsWith('M')) {
      className = filterStyles.TrafficInfoFilterItemMetroBus;
    }

    result.push({
      label: line.number,
      className: className,
    });
  }

  return result;
}

export interface TrafficInfoAccordionTag {
  label: string;
  className: string;
}

export interface TrafficInfoAccordionProps {
  index: number;
  info: TrafficInfoResponse['infos'][number];
}

const Tags = (props: { tags: TrafficInfoAccordionTag[] }): JSX.Element | null => {
  const tags = props.tags ?? [];
  if (tags.length === 0) {
    return null;
  }

  return (
    <div className={styles.TrafficInfoAccordionTags}>
      {tags.map((tag, index) => (
        <div key={index} className={classNames(styles.TrafficInfoAccordionTag, tag.className)}>
          {tag.label}
        </div>
      ))}
    </div>
  );
};

const Validity = (props: { dateFrom: Date | undefined; dateTo: Date | undefined }): JSX.Element | null => {
  const isMobile = useMediaQuery('(max-width: 599px)');
  if (!props.dateFrom && !props.dateTo) {
    return null;
  }

  const Group = isMobile ? Fragment : 'div';
  let dateFromEl: ReactNode = null;
  let dateToEl: ReactNode = null;

  if (props.dateFrom) {
    const dateFrom = dayjs(props.dateFrom);
    const dateFromDate = dateFrom.format('DD.MM.YYYY');
    const dateFromTime = dateFrom.format('HH:mm');
    const label = props.dateTo ? 'Von:' : 'Ab:';

    dateFromEl = (
      <Group>
        <span className={styles.TrafficInfoAccordionValidityLabel}>{label}</span>
        <CalendarDate />
        <span>{dateFromDate}</span>
        <CalendarTime />
        <span>{dateFromTime} Uhr</span>
      </Group>
    );
  }

  if (props.dateTo) {
    const dateTo = dayjs(props.dateTo);
    const dateToDate = dateTo.format('DD.MM.YYYY');
    const dateToTime = dateTo.format('HH:mm');
    const label = props.dateFrom ? 'bis:' : 'Bis:';

    dateToEl = (
      <Group>
        <span className={styles.TrafficInfoAccordionValidityLabel}>{label}</span>
        <CalendarDate />
        <span>{dateToDate}</span>
        <CalendarTime />
        <span>{dateToTime} Uhr</span>
      </Group>
    );
  }

  return (
    <div className={styles.TrafficInfoAccordionValidity}>
      {dateFromEl}
      {dateToEl}
    </div>
  );
};

export const TrafficInfoAccordion = (props: TrafficInfoAccordionProps): JSX.Element => {
  const id = `TrafficInfoAccordion${props.index}`;
  const matomo = useMatomo();
  const trafficInfoContext = useTrafficInfoContext();
  const [open, setOpen] = useState(isCurrentHash(id));

  const info = props.info;
  const dateFrom = info.fromDate ? new Date(info.fromDate) : undefined;
  const dateTo = info.untilDate ? new Date(info.untilDate) : undefined;
  const tags: TrafficInfoAccordionTag[] = buildTags(info, trafficInfoContext.typeFilters);

  function onChange(_: unknown, value: boolean): void {
    matomo.accordion(props.info.title, value ? 'show' : 'hide');
    setOpen(value);
  }

  return (
    <Accordion
      id={id}
      className={classNames(styles.TrafficInfoAccordion, {
        [styles.Open]: open,
      })}
      data-test-id-={`TrafficInfoAccordion${props.index}`}
      expanded={open}
      onChange={onChange}
      disableGutters
      square
    >
      <AccordionSummary className={styles.TrafficInfoAccordionSummary}>
        <div className={styles.TrafficInfoAccordionHeadlineArea}>
          <Tags tags={tags} />
          <div className={styles.TrafficInfoAccordionHeadline}>{props.info.subtitle}</div>
          <Validity dateFrom={dateFrom} dateTo={dateTo} />
        </div>
        <div className={styles.TrafficInfoAccordionChevron}>
          <AccordionChevron />
        </div>
      </AccordionSummary>
      <AccordionDetails className={styles.TrafficInfoAccordionDetails}>
        <TrafficInfoBody info={info} />
      </AccordionDetails>
    </Accordion>
  );
};
